import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete, MatOption } from '@angular/material/autocomplete';
import { ActivatedRoute, Router } from '@angular/router';
import { ClockRegister } from 'src/app/models/clock-register.model';
import { ClockRegisterReviewRequest } from 'src/app/models/clock-register-review-request.model';
import { ClockRegisterJournalClockTypeEnum } from 'src/app/enums/clock-register-journal-clock-type.enum';
import { EmployeeRequestResponsibleForAnswersRequestableTypeEnum } from 'src/app/enums/employee-request-responsible-for-answers-requestable-type.enum';
import { EmployeeRequestApprovalStatusDialogComponent } from 'src/app/components/main/shared/employee-request-approval-status-dialog/employee-request-approval-status-dialog.component';
import { EmployeeRequestResponsibleForAnswerStatusEnum } from 'src/app/enums/employee-request-responsible-for-answer-status.enum';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { UserService } from 'src/app/services/user.service';
import { ClockRegisterService } from 'src/app/services/clock-register.service';
import { UtilsService } from 'src/app/services/utils.service';
import { AddNewClockRegisterReviewRequestDialogComponent } from './add-new-clock-register-review-request-dialog/add-new-clock-register-review-request-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import { MatFormField, MatPrefix, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton, MatIconButton } from '@angular/material/button';
import { filter, firstValueFrom, map, of, switchMap } from 'rxjs';
import { EmployeeRequestApprovalStatusDialogData } from 'src/app/models/employee-request-responsible-for-answer.model';
import { ManageLocationDialogComponent } from 'src/app/components/main/shared/locations/manage-location-dialog/manage-location-dialog.component';
import { Location, ManageLocationDialogData } from 'src/app/models/location.model';
import { LocationDialogModeEnum } from 'src/app/enums/location-dialog-mode.enum';

interface ClockRegisterView extends ClockRegister {
  semaforoClass?: string;
  latest_review_request: ClockRegisterReviewRequest | null;
}

@Component({
  selector: 'app-clock-registers',
  templateUrl: './clock-registers.component.html',
  styleUrls: ['./clock-registers.component.css'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatPrefix,
    MatIcon,
    MatInput,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatLabel,
    MatSelect,
    MatTooltip,
    MatButton,
    MatIconButton,
    DatePipe,
  ],
})
export class ClockRegistersComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private api = inject(ApiService);
  private fb = inject(UntypedFormBuilder);
  private user_service = inject(UserService);
  public permissions = inject(PermissionsService);
  private clock = inject(ClockRegisterService);
  private dialog = inject(MatDialog);
  private destroy_ref = inject(DestroyRef);
  protected utils = inject(UtilsService);
  protected activated_route = inject(ActivatedRoute);
  protected router = inject(Router);
  protected readonly clock_register_journal_clock_type_enum = ClockRegisterJournalClockTypeEnum;
  protected readonly employee_request_responsible_for_answer_status_enum = EmployeeRequestResponsibleForAnswerStatusEnum;

  filter_form: UntypedFormGroup = this.fb.group({
    period: [this.activated_route.snapshot.queryParamMap.get('period') ?? this.getCurrentPeriod()],
    user_id: [this.activated_route.snapshot.queryParamMap.get('user_id') ?? '', Validators.required],
  });

  private form_persistence: Record<string, unknown> | null = null;

  user_control = new UntypedFormControl();
  user_autocomplete_control = new UntypedFormControl();

  periods = signal<string[]>(this.buildPeriods());
  users_filtered = signal<User[]>([]);
  protected readonly is_rrhh_or_admin = signal<boolean>(false);

  private clock_registers_refresh_trigger = signal(0);
  clock_registers = toSignal(
    toObservable(this.clock_registers_refresh_trigger).pipe(
      switchMap(() => {
        const { user_id, ...params } = this.filter_form.value as {
          user_id?: string | number;
          period?: string;
        };
        const has_user_id = user_id != null && user_id !== '';
        if (has_user_id) {
          return this.api.getClockRegistersByUserId(String(user_id), params).pipe(
            map(registers =>
              registers.map(register => ({
                ...register,
                semaforoClass:
                  register.journal_clock_type === ClockRegisterJournalClockTypeEnum.REST
                    ? ClockRegisterJournalClockTypeEnum.REST
                    : undefined,
                latest_review_request: register.clock_register_review_requests.at(-1) ?? null,
              }))
            )
          );
        }
        return of([] as ClockRegisterView[]);
      })
    ),
    { initialValue: [] as ClockRegisterView[] }
  );

  constructor() {
    this.initSideEffects();

    this.permissions
      .validateDepartmentObservable(['direction', 'rrhh'])
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((value: boolean) => {
        this.is_rrhh_or_admin.set(value);
        this.initCurrentUser();
      });
  }

  onUserAutocompleteSelected(event: MatAutocompleteSelectedEvent) {
    this.selectUser(event.option.value);
  }

  selectUser(user: User) {
    this.user_control.setValue(user);
  }

  displayFnUser(user: User): string {
    return user ? user.name + ' ' + user.surnames : '';
  }

  requestReview(clock: ClockRegisterView) {
    const current_location_id = clock.location_id ?? clock.location_ip?.location_id ?? clock.location_ip?.location?.id ?? null;

    this.dialog
      .open(AddNewClockRegisterReviewRequestDialogComponent, {
        width: '480px',
        data: {
          clock_register_id: clock.id,
          clock_register_created_at: clock.created_at,
          user_id: clock.user_id,
          current_location_id,
        },
      })
      .afterClosed()
      .pipe(
        filter(data => data != null),
        switchMap(data => this.api.createClockRegisterReviewRequest(data))
      )
      .subscribe(() => {
        this.refreshClockRegisters();
      });
  }

  openApprovalStatusDialog(clock_register_review_request: ClockRegisterReviewRequest) {
    this.dialog.open(EmployeeRequestApprovalStatusDialogComponent, {
      width: '800px',
      data: {
        requestable_type: EmployeeRequestResponsibleForAnswersRequestableTypeEnum.ClockRegisterReviewRequest,
        requestable_id: clock_register_review_request.id,
        requestable_status: clock_register_review_request.status,
      } as EmployeeRequestApprovalStatusDialogData,
    });
  }

  openLocationDialog(clock: ClockRegisterView) {
    const base_location = clock.location_ip?.location ?? clock.location;
    if (base_location == null) {
      return;
    }

    const location_ips_for_clock =
      clock.location_ip?.location?.location_ips?.filter(location_ip => location_ip.id === clock.location_ip?.id) ??
      base_location.location_ips ??
      [];

    const location: Location = {
      ...base_location,
      location_ips: location_ips_for_clock,
    };
    this.dialog.open(ManageLocationDialogComponent, {
      data: { mode: LocationDialogModeEnum.View, location } satisfies ManageLocationDialogData,
    });
  }

  private getCurrentPeriod() {
    return new Date().getFullYear() + '/' + (new Date().getMonth() + 1);
  }

  private buildPeriods(): string[] {
    const periods: string[] = [];
    const now = new Date();
    const start = new Date(2021, 8, 1);
    const cursor = new Date(start);
    while (cursor <= now) {
      periods.push(cursor.getFullYear() + '/' + (cursor.getMonth() + 1));
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return periods.reverse();
  }

  private refreshClockRegisters() {
    this.clock_registers_refresh_trigger.update(v => v + 1);
  }

  private initSideEffects() {
    this.filter_form.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(data => {
      if (this.form_persistence == null || JSON.stringify(this.form_persistence) != JSON.stringify(data)) {
        this.router.navigate(['/employees', 'activity', 'clock-registers'], { queryParams: data });
      }
    });

    this.activated_route.queryParams.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(params => {
      if (JSON.stringify(params) !== JSON.stringify(this.filter_form.value)) {
        const params_temp = this.utils.cloneObj(params);
        Object.keys(params_temp).forEach(param_key => {
          if (params_temp[param_key] != null && params_temp[param_key] != '' && !isNaN(+params_temp[param_key]))
            params_temp[param_key] = +params_temp[param_key];
        });
        this.filter_form.patchValue(params_temp, { emitEvent: false });
      }
      this.form_persistence = params;
      this.refreshClockRegisters();
    });

    this.user_control.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(user => {
      this.user_autocomplete_control.setValue(user);
      this.filter_form.patchValue({ user_id: user.id });
    });

    this.user_autocomplete_control.valueChanges
      .pipe(
        filter(data => data?.id == null),
        switchMap(data => this.api.getActiveEmployees({ search: data })),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe((users: User[]): void => {
        this.users_filtered.set(users);
      });

    this.clock.statusObservable.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(() => {
      this.refreshClockRegisters();
    });
  }

  private async initCurrentUser(): Promise<void> {
    const user = await firstValueFrom(this.user_service.currentUser.pipe(takeUntilDestroyed(this.destroy_ref)));
    if (!user) return;

    this.selectUser(user);
    this.users_filtered.set([user]);
    this.user_autocomplete_control.setValue(user);
    this.filter_form.patchValue({ user_id: user.id });

    if (!this.is_rrhh_or_admin()) {
      this.user_autocomplete_control.disable();
      this.user_control.disable();
    } else {
      this.user_autocomplete_control.enable();
      this.user_control.enable();
    }
  }
}
